import React, { useState, useRef } from 'react';

const LumpsumCalculatorSection = () => {
    const [lumpsumAmount, setLumpsumAmount] = useState(1000000);
    const [investmentPeriod, setInvestmentPeriod] = useState(10);
    const [expectedReturn, setExpectedReturn] = useState(12);
    const [result, setResult] = useState(null);
    const [chartData, setChartData] = useState(null);
    const resultRef = useRef(null);

    const calculateLumpsum = () => {
        const annualRate = expectedReturn / 100;
        const futureValue = lumpsumAmount * Math.pow(1 + annualRate, investmentPeriod);
        const wealthGained = futureValue - lumpsumAmount;

        // Generate chart data
        const chartPoints = [];
        for (let year = 0; year <= investmentPeriod; year++) {
            const accumulatedAmount = lumpsumAmount * Math.pow(1 + annualRate, year);

            chartPoints.push({
                year,
                invested: lumpsumAmount,
                accumulated: accumulatedAmount
            });
        }

        setChartData(chartPoints);
        setResult({
            futureValue: Math.round(futureValue),
            totalInvestment: lumpsumAmount,
            wealthGained: Math.round(wealthGained)
        });

        // Scroll to results section
        setTimeout(() => {
            resultRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const styles = {
        lumpsumCalculatorContent: {
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
        },
        description: {
            color: '#6b7280',
            fontSize: '1rem',
            lineHeight: '1.6',
            margin: '0 0 2rem 0'
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
        },
        label: {
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#374151',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        valueDisplay: {
            backgroundColor: '#374151',
            color: 'white',
            padding: '0.25rem 0.75rem',
            borderRadius: '15px',
            fontSize: '0.85rem',
            fontWeight: '600'
        },
        manualInput: {
            padding: '0.5rem 0.75rem',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#374151',
            backgroundColor: 'white',
            width: '120px',
            textAlign: 'center',
            outline: 'none',
            transition: 'border-color 0.2s ease'
        },
        sliderContainer: {
            position: 'relative',
            margin: '1rem 0'
        },
        slider: {
            width: '100%',
            height: '6px',
            borderRadius: '3px',
            background: '#e5e7eb',
            outline: 'none',
            WebkitAppearance: 'none',
            appearance: 'none'
        },
        sliderLabels: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.75rem',
            color: '#9ca3af',
            marginTop: '0.5rem'
        },
        calculateButton: {
            backgroundColor: '#22c55e',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '25px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            alignSelf: 'center',
            marginTop: '1rem'
        },
        resultSection: {
            marginTop: '3rem',
            padding: '2rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '15px',
            border: '1px solid #e5e7eb'
        },
        chartSection: {
            marginBottom: '2rem'
        },
        chartTitle: {
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '1rem',
            textAlign: 'center'
        },
        chartContainer: {
            position: 'relative',
            height: '400px',
            backgroundColor: 'white',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            padding: '20px',
            marginBottom: '2rem'
        },
        resultGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '1rem',
            textAlign: 'center'
        },
        resultItem: {
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
        },
        resultLabel: {
            fontSize: '0.9rem',
            color: '#6b7280',
            marginBottom: '0.5rem',
            fontWeight: '500'
        },
        resultValue: {
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#1a1a1a'
        },
        resultText: {
            backgroundColor: '#e3f2fd',
            padding: '1.5rem',
            borderRadius: '12px',
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#1a1a1a',
            marginTop: '1rem'
        },
        resultNote: {
            fontSize: '0.85rem',
            color: '#6b7280',
            fontStyle: 'italic',
            marginTop: '1rem'
        }
    };

    // SVG Chart Component
    const GrowthChart = ({ data }) => {
        if (!data || data.length === 0) return null;

        const maxValue = Math.max(...data.map(d => d.accumulated));
        const chartWidth = 760;
        const chartHeight = 300;
        const padding = 60;
        const plotWidth = chartWidth - padding * 2;
        const plotHeight = chartHeight - padding * 2;

        const xScale = (year) => padding + (year / investmentPeriod) * plotWidth;
        const yScale = (value) => chartHeight - padding - (value / maxValue) * plotHeight;

        // Create path for invested amount (flat line)
        const investedPath = `M ${padding} ${yScale(lumpsumAmount)} L ${chartWidth - padding} ${yScale(lumpsumAmount)}`;

        // Create path for accumulated amount (growth curve)
        const accumulatedLine = data.map((d, i) =>
            `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.accumulated)}`
        ).join(' ');

        // Create area under accumulated curve
        const accumulatedArea = data.map((d, i) =>
            `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.accumulated)}`
        ).join(' ') + ` L ${xScale(investmentPeriod)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`;

        // Generate Y-axis labels
        const yLabels = [];
        for (let i = 0; i <= 6; i++) {
            const value = (maxValue / 6) * i;
            yLabels.push({
                value: Math.round(value),
                y: yScale(value)
            });
        }

        return (
            <svg width="100%" height="400" viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
                {/* Grid lines */}
                <defs>
                    <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                        <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#f3f4f6" strokeWidth="1" />
                    </pattern>
                </defs>
                <rect width={chartWidth} height={chartHeight} fill="url(#grid)" />

                {/* Y-axis labels */}
                {yLabels.map((label, i) => (
                    <g key={i}>
                        <line x1={padding} y1={label.y} x2={chartWidth - padding} y2={label.y} stroke="#e5e7eb" strokeWidth="1" />
                        <text x={padding - 10} y={label.y + 5} textAnchor="end" fontSize="12" fill="#6b7280">
                            {(label.value / 100000).toFixed(1)}L
                        </text>
                    </g>
                ))}

                {/* X-axis labels */}
                {data.map((d, i) => (
                    <g key={i}>
                        {i % 2 === 0 && (
                            <>
                                <line x1={xScale(d.year)} y1={chartHeight - padding} x2={xScale(d.year)} y2={chartHeight - padding + 5} stroke="#6b7280" strokeWidth="1" />
                                <text x={xScale(d.year)} y={chartHeight - padding + 20} textAnchor="middle" fontSize="12" fill="#6b7280">
                                    {d.year}
                                </text>
                            </>
                        )}
                    </g>
                ))}

                {/* Accumulated amount area */}
                <path d={accumulatedArea} fill="#10b981" opacity="0.3" />

                {/* Invested amount line */}
                <path d={investedPath} fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,5" />

                {/* Accumulated amount line */}
                <path d={accumulatedLine} fill="none" stroke="#10b981" strokeWidth="3" />

                {/* Axis labels */}
                <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151">
                    Period in Years
                </text>
                <text x={25} y={chartHeight / 2} textAnchor="middle" fontSize="14" fontWeight="600" fill="#374151" transform={`rotate(-90, 25, ${chartHeight / 2})`}>
                    Amount (Rs.)
                </text>

                {/* Legend */}
                <g transform={`translate(${chartWidth - 200}, 30)`}>
                    <rect x="0" y="0" width="15" height="15" fill="#10b981" />
                    <text x="20" y="12" fontSize="12" fill="#374151">Future Value</text>
                    <line x="0" y="30" x2="15" y2="30" stroke="#3b82f6" strokeWidth="2" strokeDasharray="3,3" />
                    <text x="20" y="35" fontSize="12" fill="#374151">Initial Investment</text>
                </g>
            </svg>
        );
    };

    return (
        <>
            <style>{`
        .lumpsum-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .lumpsum-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .lumpsum-calculate-btn:hover {
          background-color: rgb(38, 220, 68) !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
      `}</style>

            <div style={styles.lumpsumCalculatorContent}>
                <p style={styles.description}>
                    Calculate how much wealth you can create by investing a one-time lump sum amount.
                </p>

                {/* Lumpsum Investment */}
                <div style={styles.inputGroup}>
                    <label style={styles.label}>
                        Lumpsum Investment (Rs.)
                        <input
                            type="number"
                            min="10000"
                            max="10000000"
                            step="10000"
                            value={lumpsumAmount}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                if (inputValue === '') {
                                    setLumpsumAmount('');
                                } else {
                                    const numValue = parseInt(inputValue);
                                    if (!isNaN(numValue)) {
                                        setLumpsumAmount(Math.max(10000, Math.min(10000000, numValue)));
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                                    setLumpsumAmount(1000000);
                                }
                            }}
                            style={styles.manualInput}
                            className="manual-input"
                            placeholder="Enter amount"
                        />
                    </label>
                    <div style={styles.sliderContainer}>
                        <input
                            type="range"
                            min="10000"
                            max="10000000"
                            step="10000"
                            value={lumpsumAmount}
                            onChange={(e) => setLumpsumAmount(parseInt(e.target.value))}
                            style={styles.slider}
                            className="lumpsum-slider"
                        />
                        <div style={styles.sliderLabels}>
                            <span>10K</span>
                            <span>2.5L</span>
                            <span>5L</span>
                            <span>7.5L</span>
                            <span>1Cr</span>
                        </div>
                    </div>
                </div>

                {/* Investment Period */}
                <div style={styles.inputGroup}>
                    <label style={styles.label}>
                        Investment period (in years)
                        <input
                            type="number"
                            min="1"
                            max="30"
                            step="1"
                            value={investmentPeriod}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                if (inputValue === '') {
                                    setInvestmentPeriod('');
                                } else {
                                    const numValue = parseInt(inputValue);
                                    if (!isNaN(numValue)) {
                                        setInvestmentPeriod(Math.max(1, Math.min(30, numValue)));
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                                    setInvestmentPeriod(10);
                                }
                            }}
                            style={styles.manualInput}
                            className="manual-input"
                            placeholder="Enter years"
                        />
                    </label>
                    <div style={styles.sliderContainer}>
                        <input
                            type="range"
                            min="1"
                            max="30"
                            step="1"
                            value={investmentPeriod}
                            onChange={(e) => setInvestmentPeriod(parseInt(e.target.value))}
                            style={styles.slider}
                            className="lumpsum-slider"
                        />
                        <div style={styles.sliderLabels}>
                            <span>1</span>
                            <span>5</span>
                            <span>10</span>
                            <span>15</span>
                            <span>20</span>
                            <span>25</span>
                            <span>30</span>
                        </div>
                    </div>
                </div>

                {/* Expected Rate of Return */}
                <div style={styles.inputGroup}>
                    <label style={styles.label}>
                        Expected rate of return (% p.a)
                        <input
                            type="number"
                            min="1"
                            max="25"
                            step="0.1"
                            value={expectedReturn}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                if (inputValue === '') {
                                    setExpectedReturn('');
                                } else {
                                    const numValue = parseFloat(inputValue);
                                    if (!isNaN(numValue)) {
                                        setExpectedReturn(Math.max(1, Math.min(25, numValue)));
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                    setExpectedReturn(12);
                                }
                            }}
                            style={styles.manualInput}
                            className="manual-input"
                            placeholder="Enter rate"
                        />
                    </label>
                    <div style={styles.sliderContainer}>
                        <input
                            type="range"
                            min="1"
                            max="25"
                            step="0.1"
                            value={expectedReturn}
                            onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
                            style={styles.slider}
                            className="lumpsum-slider"
                        />
                        <div style={styles.sliderLabels}>
                            <span>1</span>
                            <span>5</span>
                            <span>10</span>
                            <span>15</span>
                            <span>20</span>
                            <span>25</span>
                        </div>
                    </div>
                </div>

                {/* Calculate Button */}
                <button
                    style={styles.calculateButton}
                    className="lumpsum-calculate-btn"
                    onClick={calculateLumpsum}
                >
                    Calculate
                </button>

                {/* Results */}
                {result && (
                    <div ref={resultRef} style={styles.resultSection}>
                        {/* Growth Chart */}
                        <div style={styles.chartSection}>
                            <h3 style={styles.chartTitle}>Growth Chart</h3>
                            <GrowthChart data={chartData} />
                        </div>

                        {/* Result Summary */}
                        <div style={styles.resultGrid}>
                            <div style={styles.resultItem}>
                                <div style={styles.resultLabel}>Initial Investment</div>
                                <div style={styles.resultValue}>₹{result.totalInvestment.toLocaleString()}</div>
                            </div>
                            <div style={styles.resultItem}>
                                <div style={styles.resultLabel}>Wealth Gained</div>
                                <div style={styles.resultValue}>₹{result.wealthGained.toLocaleString()}</div>
                            </div>
                            <div style={styles.resultItem}>
                                <div style={styles.resultLabel}>Future Value</div>
                                <div style={styles.resultValue}>₹{result.futureValue.toLocaleString()}</div>
                            </div>
                        </div>

                        {/* Result Description */}
                        <div style={styles.resultText}>
                            If you invest Rs. <strong>{lumpsumAmount.toLocaleString()}/-</strong> as a lump sum for a period of <strong>{investmentPeriod}</strong> years,
                            your investment will grow to Rs. <strong>{result.futureValue.toLocaleString()}/-</strong>.
                            You will gain Rs. <strong>{result.wealthGained.toLocaleString()}/-</strong> from your initial investment.
                        </div>

                        <div style={styles.resultNote}>
                            Note: Based on annual compounding
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default LumpsumCalculatorSection;